import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, flatMap, switchMap, tap } from 'rxjs/operators';
import * as fromLookups from '../../lookups';
import * as fromShared from '../../shared';
import {
    DisplayMessageMinimum,
    IStatusMessage,
    ResponseStatus
} from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { FixedPriceService } from '../fixed-price.service';
import * as fromFixedPrice from './actions';

@Injectable()
export class FixedPriceEffects {
    constructor(
        private actions$: Actions,
        private lookups: Store<fromLookups.LookupState>,
        @Inject(FixedPriceService) private service: FixedPriceService,
        @Inject(LoggingService) private log: LoggingService
    ) {}

    LoadFixedPrice$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromFixedPrice.LoadFixedPrice>(
                fromFixedPrice.ActionTypes.LoadFixedPrice
            ),
            tap(action =>
                this.log.debug({
                    type: 'FixedPriceEffects:LoadFixedPrice$:filtered',
                    action
                })
            ),
            // , withLatestFrom(this.lookups.pipe(select(fromLookups.getTasksLookup)))
            switchMap(x =>
                this.service.getFixedPrice(x.payload).pipe(
                    flatMap(exp => [
                        new fromFixedPrice.SetFixedPrice(exp),
                        new fromShared.Errors.RemoveErrors(
                            exp
                                .filter(
                                    i =>
                                        i.Status.StatusCode ===
                                        ResponseStatus.Success
                                )
                                .map(i => i.UniqueID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...exp
                                    .filter(
                                        a =>
                                            a.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    UpdateFixedPrice$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromFixedPrice.UpdateFixedPrice>(
                fromFixedPrice.ActionTypes.UpdateFixedPrice
            ),
            tap(x =>
                this.log.verbose({
                    type: 'FixedPriceEffects:UpdateFixedPrice$',
                    x
                })
            ),
            switchMap(a =>
                this.service.putFixedPrice([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromFixedPrice.UpdateFixedPriceSuccess(resp),
                        // , new fromShared.Errors.SetErrors(resp.ErrorMessages)
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...resp.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    DeleteFixedPrice$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromFixedPrice.DeleteFixedPrice>(
                fromFixedPrice.ActionTypes.DeleteFixedPrice
            ),
            switchMap(a =>
                this.service.deleteFixedPrice([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromFixedPrice.DeleteFixedPriceSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.EntityID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...resp.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
}
